/**
 * PlayerFilterBar — the thin bar the map bar's Players segment opens just
 * beneath it (maintainer, 2026-11-03): a chip per player (free-for-all, the
 * name in the player's colour) or per team (team games, the members' colour
 * dots), in the log filter's chip style. Chips toggle; several can be on, and
 * the map brings forward the union of their land. The bar closes from the
 * map bar (Players again, or Game / Regions / ★); there is no ✕, so the
 * chips get the whole width (maintainer, 2026-11-03).
 */
import React from 'react'
import { teamShape, type TeamId } from '@warcarp/api-types'
import { View } from 'react-native'
import { FilterChip } from '../game/FilterChip'
import { balancedRows, type PlayerFilterOption } from '../../lib/playerFilter'
import { logNameColor } from '../../lib/playerTextColor'
import { useTheme } from '../../hooks/useTheme'

/** Side padding of the chip area: the chips use the rest of the bar's width. */
const SIDE_PADDING = 8

export function PlayerFilterBar({
  options,
  selected,
  onToggle,
  team,
}: {
  options: readonly PlayerFilterOption[]
  selected: readonly string[]
  onToggle: (key: string) => void
  /** Team chips (team games) rather than player chips. */
  team: boolean
}) {
  const { theme } = useTheme()
  const textTheme = theme === 'dark' ? 'dark' : 'light'
  const [rowWidth, setRowWidth] = React.useState(0)
  const [widths, setWidths] = React.useState<Record<string, number>>({})
  const rowsOf = (n: number): number[][] => {
    const all = Array.from({ length: n }, (_, i) => i)
    const w = options.map((o) => widths[o.key])
    if (!rowWidth || w.some((x) => x === undefined)) return [all]
    return balancedRows(w as number[], rowWidth)
  }
  const chip = (option: PlayerFilterOption) => {
    const on = selected.includes(option.key)
    return (
      <View key={option.key} onLayout={(e) => { const width = Math.ceil(e.nativeEvent.layout.width); setWidths((cur) => (cur[option.key] === width ? cur : { ...cur, [option.key]: width })) }}>
        <FilterChip
          testID={`player-filter-${option.key}`}
          label={option.label}
          active={on}
          onPress={() => onToggle(option.key)}
          // Bigger dots; a team's members drawn in the team's shape.
          dotColors={option.colors}
          dotSize={team ? 13 : 12}
          // On: a ring in the player's colour (a team's: its first member's).
          activeRingColor={option.colors[0]}
          dotShape={team ? teamShape(option.key.replace(/^team:/, '') as TeamId) : undefined}
          labelColor={team ? undefined : logNameColor(option.colors[0] ?? '#888888', textTheme)}
          accessibilityLabel={on ? `${option.label}: shown on the map` : `Show ${option.label}'s territories`}
        />
      </View>
    )
  }
  return (
    <View
      testID="player-filter-bar"
      accessibilityLabel={team ? 'Filter the map by team' : 'Filter the map by player'}
      className="bg-paper border-b border-olive flex-row items-center"
      style={{ minHeight: 34 }}
    >
      {/* Centred; one line whenever the measured chips (margins included)
          fit the full width, otherwise the fewest lines that fit, as evenly
          as possible (9 chips: 3 + 3 + 3) rather than a short last line
          (maintainer, 2026-11-03). The measured width is the content box. */}
      <View
        testID="player-filter-rows"
        onLayout={(e) => setRowWidth(e.nativeEvent.layout.width)}
        style={{ flex: 1, marginHorizontal: SIDE_PADDING, paddingTop: 5, paddingBottom: 1 }}
      >
        {rowsOf(options.length).map((row, r) => (
          <View key={r} style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
            {row.map((i) => chip(options[i]!))}
          </View>
        ))}
      </View>
    </View>
  )
}
